import genKey from 'draft-js/lib/generateRandomKey';

export const DEFAULT_ROW_COUNT = 3;
export const DEFAULT_COLUMN_COUNT = 3;
export const MIN_COLUMN_WIDTH = 48;

function emptyContent() {
  return {
    blocks: [
      { key: genKey(), text: '', type: 'unstyled', depth: 0, inlineStyleRanges: [], entityRanges: [], data: {} },
    ],
    entityMap: {},
  };
}

export function emptyCell() {
  return { key: genKey(), content: emptyContent() };
}

function emptyRow(columnCount) {
  return { key: genKey(), cells: [...Array(columnCount)].map(emptyCell) };
}

export function createTable(rowCount = DEFAULT_ROW_COUNT, columnCount = DEFAULT_COLUMN_COUNT) {
  return {
    headerRow: false,
    headerColumn: false,
    columnWidths: [...Array(columnCount)].map(() => null),
    rows: [...Array(rowCount)].map(() => emptyRow(columnCount)),
  };
}

export function getColumnCount(table) {
  return table.rows[0]?.cells.length || 0;
}

// Entity data survives a convertToRaw/convertFromRaw round trip as plain JSON, but content authored
// elsewhere (imports, seeds, hand-written fixtures) may omit the react keys or a columnWidths entry.
// Returns the original object untouched when nothing is missing, so cell identity is preserved.
export function normalizeTable(table) {
  if (!table?.rows?.length) return createTable();

  let changed = false;
  const rows = table.rows.map(row => {
    let rowChanged = !row.key;
    const cells = row.cells.map(cell => {
      if (cell.key) return cell;
      rowChanged = true;
      return { ...cell, key: genKey() };
    });
    if (!rowChanged) return row;
    changed = true;
    return { ...row, key: row.key || genKey(), cells };
  });

  const columnCount = rows[0]?.cells.length || 0;
  let columnWidths = table.columnWidths;
  if (!Array.isArray(columnWidths) || columnWidths.length !== columnCount) {
    columnWidths = [...Array(columnCount)].map((_, i) => columnWidths?.[i] ?? null);
    changed = true;
  }

  return changed ? { ...table, rows, columnWidths } : table;
}

function insertAt(array, index, value) {
  const next = [...array];
  next.splice(index, 0, value);
  return next;
}

function removeAt(array, index) {
  return array.filter((_, i) => i !== index);
}

export function insertRow(table, index) {
  return { ...table, rows: insertAt(table.rows, index, emptyRow(getColumnCount(table))) };
}

export function deleteRow(table, index) {
  if (table.rows.length <= 1) return table;
  return { ...table, rows: removeAt(table.rows, index) };
}

export function insertColumn(table, index) {
  return {
    ...table,
    columnWidths: insertAt(table.columnWidths, index, null),
    rows: table.rows.map(row => ({ ...row, cells: insertAt(row.cells, index, emptyCell()) })),
  };
}

export function deleteColumn(table, index) {
  if (getColumnCount(table) <= 1) return table;
  return {
    ...table,
    columnWidths: removeAt(table.columnWidths, index),
    rows: table.rows.map(row => ({ ...row, cells: removeAt(row.cells, index) })),
  };
}

export function setColumnWidth(table, index, width) {
  const columnWidths = [...table.columnWidths];
  columnWidths[index] = width && Math.max(Math.round(width), MIN_COLUMN_WIDTH);
  return { ...table, columnWidths };
}

// Only the edited cell (and its row) get new object identities, so every other TableCell bails out
// of re-rendering on each keystroke.
export function setCellContent(table, rowIndex, columnIndex, content) {
  const row = table.rows[rowIndex];
  const cell = row?.cells[columnIndex];
  if (!cell || cell.content === content) return table;

  const cells = [...row.cells];
  cells[columnIndex] = { ...cell, content };
  const rows = [...table.rows];
  rows[rowIndex] = { ...row, cells };
  return { ...table, rows };
}

export function isHeaderCell(table, rowIndex, columnIndex) {
  return !!((table.headerRow && rowIndex === 0) || (table.headerColumn && columnIndex === 0));
}

// Walks the grid in reading order, so Tab from the last cell of a row lands on the first cell of the
// next one. Returns null past either end, letting the caller append a row or release focus.
export function getAdjacentCell(table, rowIndex, columnIndex, direction) {
  const columnCount = getColumnCount(table);
  const rowCount = table.rows.length;

  switch (direction) {
    case 'next': {
      if (columnIndex < columnCount - 1) return { rowIndex, columnIndex: columnIndex + 1 };
      return rowIndex < rowCount - 1 ? { rowIndex: rowIndex + 1, columnIndex: 0 } : null;
    }
    case 'previous': {
      if (columnIndex > 0) return { rowIndex, columnIndex: columnIndex - 1 };
      return rowIndex > 0 ? { rowIndex: rowIndex - 1, columnIndex: columnCount - 1 } : null;
    }
    case 'up':
      return rowIndex > 0 ? { rowIndex: rowIndex - 1, columnIndex } : null;
    case 'down':
      return rowIndex < rowCount - 1 ? { rowIndex: rowIndex + 1, columnIndex } : null;
    default:
      return null;
  }
}
